// "cancelled-user-gating" rollout (klap-api helpers/cancelled_user_gating.ts):
// a lapsed subscriber (their B2C subscription ended, not a free user who
// never paid) in the gated arm can't create clips, play them, download them
// or post them until they resubscribe; klap-api answers those requests with
// a 402 `subscription_ended`. klap-api evaluates the arm, the only place it
// is evaluated: GET /billing/subscription returns it as `cancelled_access`,
// so the app never reads the flag itself. The clips aren't deleted: the
// app shows them locked behind the resubscribe dialog. Delete with the
// rollout.

export const CANCELLED_USER_ERROR_CODE = "subscription_ended";

export type CancelledAccessState = {
  variant: "control" | "test";
  gated: boolean;
  /** The plan the subscription was on, when known. */
  plan: string | null;
  /** Unix seconds the subscription ended. */
  ended_at: number | null;
};

export type CancelledAccessResponse = {
  cancelled_access?: CancelledAccessState | null;
};

// A 402 from a create/download/publish route that the gate answers.
export const isCancelledUserResponse = (
  status: number,
  body?: { error_code?: string } | null,
): boolean => status === 402 && body?.error_code === CANCELLED_USER_ERROR_CODE;

// Profile statuses that still pay: klap-api never gates them, so the app
// doesn't wait for GET /billing/subscription before showing their clips.
const PAYING_STATUSES = ["active", "trialing", "past_due", "paused", "unpaid"];

export const mayBeLapsed = (
  profile?: {
    customerId?: string | null;
    subscriptionStatus?: string | null;
    isGuest?: boolean | null;
  } | null,
): boolean =>
  !!profile &&
  !profile.isGuest &&
  !!profile.customerId &&
  !PAYING_STATUSES.includes(profile.subscriptionStatus ?? "");

// Gated: the arm says so and the profile hasn't become paying since the
// cached answer (a resubscribe updates the profile first).
export const isCancelledAccessGated = (
  profile: Parameters<typeof mayBeLapsed>[0],
  response?: CancelledAccessResponse | null,
): boolean => mayBeLapsed(profile) && !!response?.cancelled_access?.gated;
